import { api, ApiError } from './api';

/** The server explains refused uploads (wrong type, not a real image, link expired); show that to the user. */
export async function uploadError(res: Response): Promise<string> {
  try { const b = (await res.json()) as { error?: { message?: string } }; if (b.error?.message) return b.error.message; } catch { /* S3 returns XML */ }
  return `Upload failed (${res.status})`;
}

/** Same rule as the server: https everywhere; plain http only for this machine (local development). */
export const isMediaUrl = (v: string) => /^https:\/\/\S+$/.test(v.trim()) || /^http:\/\/(localhost|127\.0\.0\.1)(:\d+)?\/\S*$/.test(v.trim());

export const IMAGE_TYPES = ['image/jpeg', 'image/png', 'image/webp', 'image/avif'];

export const VIDEO_TYPES = ['video/mp4', 'video/webm'];

/** Banner video (MP4/WebM, ≤ 30 MB); returns its public URL. */
export async function uploadVideo(file: File): Promise<string> {
  if (!VIDEO_TYPES.includes(file.type)) throw new Error('Use an MP4 or WebM video');
  if (file.size > 30 * 1024 * 1024) throw new Error('Videos must be 30 MB or smaller');
  return send(file, 'banner');
}

/** Direct-to-storage upload; returns the public (CDN) URL. */
export async function uploadImage(file: File, purpose: 'banner' | 'category' | 'cms'): Promise<string> {
  if (!IMAGE_TYPES.includes(file.type)) throw new Error('Use a JPEG, PNG, WebP or AVIF image');
  if (file.size > 5 * 1024 * 1024) throw new Error('Images must be 5 MB or smaller');
  return send(file, purpose);
}

async function send(file: File, purpose: 'banner' | 'category' | 'cms'): Promise<string> {
  try {
    const pre = await api<{ url: string; headers: Record<string, string>; publicUrl: string }>('/admin/uploads', { method: 'POST', body: { contentType: file.type, size: file.size, purpose } });
    const put = await fetch(pre.url, { method: 'PUT', headers: pre.headers, body: file });
    if (!put.ok) throw new Error(await uploadError(put));
    return pre.publicUrl;
  } catch (e) {
    if (e instanceof ApiError && e.code === 'STORAGE_NOT_CONFIGURED') throw new Error('Image storage is not set up yet — paste an https image URL instead.');
    throw e;
  }
}

/** <input type="datetime-local"> value ↔ ISO instant (browser's local time zone). */
export const toLocalInput = (iso: string | null | undefined) => {
  if (!iso) return '';
  const d = new Date(iso);
  return new Date(d.getTime() - d.getTimezoneOffset() * 60_000).toISOString().slice(0, 16);
};
export const fromLocalInput = (v: string) => (v ? new Date(v).toISOString() : null);
